// Инициативы цикла: создание, голосование, модалки деталей, повестка,
// showcase-карточки. Вынесено из CycleTab.tsx без изменения поведения
// (Фаза 1, разбор монолита).

import { useState, useEffect, type FormEvent } from "react";
import {
  api,
  type CampaignSetup,
  type GameCycle,
  type Initiative,
  type InitiativeVoteStance,
} from "../api";
import { actionTypeLabel } from "./ActionForm";
import { useAssetContent } from "./NegotiationRooms";
import {
  Avatar,
  GAME_UI_ASSET_BASE,
  MockupSectionTitle,
  SectionTitle,
  actionNarrativeStatusText,
  factionStanceLabel,
  formatDuration,
  fundingLabel,
  previewText,
  readDraft,
  riskLabel,
  sameIds,
  statusLabel,
  voteLabel,
  writeDraft,
  type Props,
  type Section,
} from "./shared";

interface InitiativeDraft {
  title: string;
  description: string;
  linkedActionIds: string[];
  linkedAgreementIds: string[];
}
function ActionInitiativesPanel(props: Props & { onNavigate?: (section: Section) => void }) {
  const cycle = props.setup.cycle.current!;
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const initiatives = cycle.initiatives.filter((item) => item.status !== "MERGED" && item.consolidatedIntoId == null);
  const ownInitiative = initiatives.find((item) => item.isMine);
  const currentUserId = props.setup.members.find((member) => member.isCurrentUser)?.userId;
  const mode: CouncilInitiativeShowcaseMode = cycle.phase === "VOTING" ? "voting" : "agenda";
  return (
    <section className="cc-action-results-section cc-action-initiatives-panel cc-mockup-action-section">
      <MockupSectionTitle eyebrow="Инициативы" title={cycle.phase === "VOTING" ? "Голосование" : "Решения Совета"} />
      {cycle.canEditInitiative && (
        <div className="cc-inline-actions cc-action-initiative-create-row">
          <button type="button" className="primary" onClick={() => setEditorOpen(true)}>{ownInitiative ? "Редактировать мою инициативу" : "Создать инициативу"}</button>
          <small>Инициативы закрепляют результаты действий как решения Совета.</small>
        </div>
      )}
      {initiatives.length === 0 ? (
        <p className="cc-empty-inline">Инициатив пока нет.</p>
      ) : (
        <div className="cc-initiative-compact-selector">
          {initiatives.map((item, index) => (
            <CouncilInitiativeSelectorCard
              campaignId={props.campaignId}
              item={item}
              active={selectedInitiative?.id === item.id}
              index={index + 1}
              key={item.id}
              onSelect={() => setSelectedInitiative(item)}
            />
          ))}
        </div>
      )}
      {editorOpen && (
        <div className="cc-modal-backdrop cc-action-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cc-modal-card cc-initiative-editor-modal">
            <header className="cc-modal-header">
              <button type="button" className="cc-modal-close" onClick={() => setEditorOpen(false)}>
                ×
              </button>
            </header>
            <CouncilInitiativeCreatePanel {...props} />
          </div>
        </div>
      )}
      {selectedInitiative && (
        <InitiativeDetailModal
          campaignId={props.campaignId}
          item={selectedInitiative}
          canManage={props.setup.canManage}
          action={props.action}
          mode={mode}
          currentUserId={currentUserId}
          memberCount={cycle.memberCount}
          votingDeadlineAt={cycle.votingDeadlineAt}
          onClose={() => setSelectedInitiative(null)}
        />
      )}
    </section>
  );
}
function AttachmentPicker({
  title,
  items,
  selected,
  onChange,
}: {
  title: string;
  items: Array<{
    id: string;
    title: string;
    detail: string;
    disabled?: boolean;
  }>;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  return (
    <fieldset className="cc-attachment-picker">
      <legend>
        {title} <small>необязательно</small>
      </legend>
      {items.length === 0 ? (
        <p>Можно сохранить инициативу без вложений.</p>
      ) : (
        items.map((item) => (
          <label key={item.id} className={item.disabled ? "disabled" : ""}>
            <input
              type="checkbox"
              disabled={item.disabled}
              checked={selected.includes(item.id)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...selected, item.id]
                    : selected.filter((id) => id !== item.id),
                )
              }
            />
            <span>
              <strong>{item.title}</strong>
              <small>{item.detail}</small>
            </span>
          </label>
        ))
      )}
    </fieldset>
  );
}
function CouncilInitiativeCreatePanel({ campaignId, setup, action }: Props) {
  const cycle = setup.cycle.current!;
  const existing = cycle.initiatives.find((item) => item.isMine && item.status !== "MERGED");
  const key = `cc:${campaignId}:${cycle.id}:initiative`;
  const initial = initiativeDraft(existing, setup);
  const [draft, setDraft] = useState<InitiativeDraft>(() => readDraft(key, initial));
  useEffect(() => writeDraft(key, draft), [key, draft]);
  useEffect(() => {
    if (
      existing &&
      existing.updatedAt !== readDraft<{ source?: string }>(`${key}:server`, {}).source
    ) {
      setDraft((current) =>
        current.title || current.description ? current : initiativeDraft(existing, setup),
      );
      writeDraft(`${key}:server`, { source: existing.updatedAt });
    }
  }, [existing?.updatedAt]);
  const ownActions = cycle.actions.filter((item) => item.isMine);
  const agreements = cycle.agreements;
  const assessmentStale = Boolean(
    existing &&
      (draft.title.trim() !== existing.title ||
        draft.description.trim() !== existing.description ||
        !sameIds(draft.linkedActionIds, existing.linkedActionIds) ||
        !sameIds(draft.linkedAgreementIds, existing.linkedAgreementIds)),
  );
  async function submit(event: FormEvent) {
    event.preventDefault();
    await action("Оцениваем и сохраняем инициативу…", () => api.saveInitiative(campaignId, draft));
  }
  if (cycle.phase !== "AGENDA") {
    return <p className="cc-empty-inline">Создание инициатив откроется на этапе Совета.</p>;
  }
  return (
    <div className="cc-council-section-stack">
      <section className="cc-block cc-initiative-editor cc-room-main-card" aria-label="Создание инициативы">
        <SectionTitle
          icon="✎"
          eyebrow="Решение Совета"
          title={existing ? "Ваша инициатива" : "Создать инициативу"}
        />
        {existing && (
          <InitiativeImage
            campaignId={campaignId}
            initiative={existing}
            canManage={setup.canManage}
            action={action}
          />
        )}
        <form className="cc-council-initiative-form" onSubmit={submit}>
          <label>
            Название инициативы
            <input
              value={draft.title}
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              placeholder="Например: открыть резервные водные пункты"
              maxLength={120}
            />
          </label>
          <label>
            Что именно должен решить Совет
            <textarea
              rows={5}
              value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            />
          </label>
          {existing ? (
            <InitiativeAssessmentView item={existing} stale={assessmentStale} />
          ) : (
            <div className="cc-assessment-placeholder">
              <strong>Системная оценка появится после сохранения</strong>
              <span>ИИ сформулирует ожидаемый результат по вашему описанию и определит ресурсы, исполнителей, фракции и риск.</span>
            </div>
          )}
          <AttachmentPicker
            title="Результаты ваших действий"
            items={ownActions.map((item) => ({
              id: item.id,
              title: item.narrativeStatus === "READY" ? item.chronicleTitle || actionTypeLabel(item.actionType) : actionTypeLabel(item.actionType),
              detail:
                item.narrativeStatus !== "READY"
                  ? actionNarrativeStatusText(item.narrativeStatus, item.narrativeNextAttemptAt)
                  : item.resolutionStatus === "PENDING_DAY_END"
                    ? "Окончательный ответ появится после завершения дня."
                    : item.achievementText || item.followUpText || "Результат можно использовать при обосновании инициативы.",
              disabled: item.narrativeStatus !== "READY",
            }))}
            selected={draft.linkedActionIds}
            onChange={(ids) => setDraft({ ...draft, linkedActionIds: ids })}
          />
          <AttachmentPicker
            title="Договорённости"
            items={agreements.map((item) => ({ id: item.id, title: item.title, detail: item.terms }))}
            selected={draft.linkedAgreementIds}
            onChange={(ids) => setDraft({ ...draft, linkedAgreementIds: ids })}
          />
          <div className="cc-inline-actions">
            <button
              className="primary"
              disabled={
                !cycle.canEditInitiative ||
                draft.title.trim().length < 3 ||
                draft.description.trim().length < 10
              }
            >
              {existing ? "Пересчитать и сохранить" : "Рассчитать и сохранить"}
            </button>
            {existing && (
              <button
                type="button"
                onClick={() => void action("Отзываем инициативу…", () => api.withdrawInitiative(campaignId))}
              >
                Отозвать
              </button>
            )}
            <InitiativeAgendaControlButtons campaignId={campaignId} cycle={cycle} action={action} />
          </div>
        </form>
      </section>
    </div>
  );
}
function InitiativeAgendaControlButtons({
  campaignId,
  cycle,
  action,
}: {
  campaignId: string;
  cycle: GameCycle;
  action: Props["action"];
}) {
  if (cycle.phase !== "AGENDA") return null;
  return cycle.myAgendaReady ? (
    <button
      type="button"
      onClick={() =>
        void action("Возвращаемся к подготовке инициативы…", () =>
          api.setAgendaReadiness(campaignId, false),
        )
      }
    >
      Продолжить подготовку
    </button>
  ) : (
    <button
      type="button"
      className="primary"
      disabled={!cycle.canMarkAgendaReady}
      onClick={() =>
        void action("Готовим бюллетень…", () =>
          api.setAgendaReadiness(campaignId, true),
        )
      }
    >
      Готов к голосованию
    </button>
  );
}
function InitiativeDetailModal({
  campaignId,
  item,
  canManage,
  action,
  mode,
  currentUserId,
  memberCount,
  votingDeadlineAt,
  onClose,
}: {
  campaignId: string;
  item: Initiative;
  canManage: boolean;
  action: Props["action"];
  mode: CouncilInitiativeShowcaseMode;
  currentUserId?: string;
  memberCount: number;
  votingDeadlineAt?: string;
  onClose: () => void;
}) {
  const authors =
    item.coauthors.length > 0
      ? item.coauthors
      : [
          {
            campaignMemberId: item.authorCampaignMemberId,
            userId: item.authorUserId,
            displayName: item.authorDisplayName,
            avatar: item.authorAvatar,
          },
        ];
  return (
    <div className="cc-modal-backdrop cc-initiative-detail-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cc-modal-card cc-initiative-detail-modal">
        <header className="cc-modal-header">
          <div>
            <span>{mode === "voting" ? "Голосование" : statusLabel(item.status)}</span>
            <h2>{item.title}</h2>
          </div>
          <button type="button" className="cc-modal-close" onClick={onClose}>×</button>
        </header>
        <div className="cc-initiative-detail-content">
          <div className="cc-initiative-detail-authors">
            <div className="cc-avatar-stack">
              {authors.map((author) => (
                <Avatar
                  campaignId={campaignId}
                  memberId={author.campaignMemberId}
                  name={author.displayName}
                  avatar={author.avatar}
                  key={author.userId}
                />
              ))}
            </div>
            <strong>{authors.length > 1 ? `Соавторы: ${authors.map((author) => author.displayName).join(", ")}` : authors[0].displayName}</strong>
            {item.isMine && <b>Моя</b>}
          </div>
          <InitiativeImage campaignId={campaignId} initiative={item} canManage={canManage} action={action} />
          <p>{item.description}</p>
          <div className="cc-outcome">
            <strong>Ожидаемый результат</strong>
            {item.intendedOutcome}
          </div>
          {item.status !== "MERGED" && <InitiativeAssessmentView item={item} />}
          <InitiativeVoteSummary item={item} memberCount={memberCount} deadline={votingDeadlineAt} mode={mode} />
          <InitiativeModalVoteControls
            campaignId={campaignId}
            item={item}
            mode={mode}
            currentUserId={currentUserId}
            action={action}
          />
          {item.sourceInitiatives.length > 1 && (
            <details className="cc-initiative-sources">
              <summary>Из каких инициатив собран проект</summary>
              <ul>
                {item.sourceInitiatives.map((source) => (
                  <li key={source.id}>
                    <strong>{source.title}</strong>
                    <span>{source.authorDisplayName}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {item.fundingExplanation && (
            <div className={`cc-funding-note ${item.fundingStatus.toLowerCase()}`}>
              <strong>{fundingLabel(item.fundingStatus)}</strong>
              <p>{item.fundingExplanation}</p>
            </div>
          )}
          <small>
            Прикреплено действий: {item.linkedActionIds.length} · договорённостей: {item.linkedAgreementIds.length}
          </small>
        </div>
      </div>
    </div>
  );
}
function InitiativeModalVoteControls({
  campaignId,
  item,
  mode,
  currentUserId,
  action,
}: {
  campaignId: string;
  item: Initiative;
  mode: CouncilInitiativeShowcaseMode;
  currentUserId?: string;
  action: Props["action"];
}) {
  const [optimisticVote, setOptimisticVote] = useState<InitiativeVoteStance | null>(item.myVote ?? null);
  const [pendingVote, setPendingVote] = useState<InitiativeVoteStance | null>(null);

  useEffect(() => {
    setOptimisticVote(item.myVote ?? null);
    setPendingVote(null);
  }, [item.id, item.myVote]);

  if (mode !== "voting") return null;
  const isCoauthor = item.isMine || item.coauthors.some((author) => author.userId === currentUserId);
  if (isCoauthor) {
    return <div className="cc-auto-support">Ваше авторство автоматически считается голосом «за».</div>;
  }

  const saveVote = async (stance: InitiativeVoteStance) => {
    setOptimisticVote(stance);
    setPendingVote(stance);
    const saved = await action("Сохраняем голос…", () => api.castInitiativeVote(campaignId, item.id, stance));
    if (!saved) setOptimisticVote(item.myVote ?? null);
    setPendingVote(null);
  };

  return (
    <div className="cc-vote-controls cc-initiative-modal-votes">
      {(["SUPPORT", "ABSTAIN", "OPPOSE"] as InitiativeVoteStance[]).map((stance) => (
        <button
          type="button"
          className={optimisticVote === stance ? "active" : ""}
          aria-pressed={optimisticVote === stance}
          disabled={pendingVote != null}
          key={stance}
          onClick={() => void saveVote(stance)}
        >
          {pendingVote === stance ? "Сохраняем…" : voteLabel(stance)}
        </button>
      ))}
    </div>
  );
}
type CouncilInitiativeShowcaseMode = "agenda" | "voting";
function CouncilInitiativeSelectorCard({
  campaignId,
  item,
  active,
  index,
  onSelect,
}: {
  campaignId: string;
  item: Initiative;
  active: boolean;
  index: number;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`cc-initiative-selector-card${active ? " active" : ""}${item.isMine ? " mine" : ""}`}
      onClick={onSelect}
      aria-current={active ? "true" : undefined}
    >
      <InitiativeThumb campaignId={campaignId} initiative={item} index={index} />
      <span>
        <strong>{item.title}</strong>
        <small>{previewText(item.description || item.intendedOutcome, 92)}</small>
      </span>
      <em>{item.isMine ? "Моя" : statusLabel(item.status)}</em>
      <i aria-hidden="true">›</i>
    </button>
  );
}
function InitiativeThumb({ campaignId, initiative, index }: { campaignId: string; initiative: Initiative; index: number }) {
  const asset = initiative.images?.[0];
  const src = useAssetContent(campaignId, asset);
  return (
    <span className={`cc-initiative-selector-thumb${src ? " ready" : ""}`}>
      {src ? (
        <img src={src} alt="" />
      ) : (
        <>
          <img src={`${GAME_UI_ASSET_BASE}/icon_vote.png`} alt="" />
          <b>{index}</b>
        </>
      )}
    </span>
  );
}
function InitiativeVoteSummary({
  item,
  memberCount,
  deadline,
  mode,
}: {
  item: Initiative;
  memberCount: number;
  deadline?: string;
  mode: CouncilInitiativeShowcaseMode;
}) {
  const pendingVotes = Math.max(0, memberCount - item.supportCount - item.opposeCount - item.abstainCount);
  return (
    <div className="cc-initiative-vote-summary">
      <div className="support"><span>Да</span><b>{item.supportCount}</b></div>
      <div className="oppose"><span>Нет</span><b>{item.opposeCount}</b></div>
      <div className="pending"><span>{mode === "voting" ? "Ждут" : "Участн."}</span><b>{mode === "voting" ? pendingVotes : memberCount}</b></div>
      <small>
        {mode === "voting" && deadline ? <InitiativeDeadlineInline value={deadline} /> : statusLabel(item.status)}
        {item.abstainCount > 0 && ` · воздержались: ${item.abstainCount}`}
      </small>
    </div>
  );
}
function InitiativeDeadlineInline({ value }: { value: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const remaining = new Date(value).getTime() - now;
  return <span>Осталось: {remaining <= 0 ? "срок истёк" : formatDuration(remaining)}</span>;
}
function InitiativeImage({
  campaignId,
  initiative,
  canManage,
  action,
}: {
  campaignId: string;
  initiative: Initiative;
  canManage: boolean;
  action: Props["action"];
}) {
  const asset = initiative.images?.[0];
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!asset || asset.status !== "READY") {
      setSrc(null);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    void api
      .cycleImageContent(campaignId, asset.id)
      .then((blob) => {
        if (!cancelled) {
          url = URL.createObjectURL(blob);
          setSrc(url);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [asset?.id, asset?.status, campaignId]);

  if (src) {
    return (
      <figure className="cc-initiative-image-card">
        <img src={src} alt="Иллюстрация инициативы" />
        <figcaption>
          <span>Сцена решения Совета</span>
          {canManage && asset && (
            <button
              type="button"
              className="cc-image-regenerate"
              onClick={() =>
                void action("Создаём новую иллюстрацию инициативы…", () =>
                  api.regenerateCycleImage(campaignId, asset.id),
                )
              }
            >
              Перегенерировать
            </button>
          )}
        </figcaption>
      </figure>
    );
  }

  return (
    <div className={`cc-initiative-image-placeholder${asset?.status === "FAILED" ? " failed" : ""}`}>
      <img src={`${GAME_UI_ASSET_BASE}/icon_vote.png`} alt="" />
      <div>
        <strong>
          {asset?.status === "FAILED"
            ? "Иллюстрация инициативы не создана"
            : "Иллюстрация инициативы готовится"}
        </strong>
        <small>
          {asset
            ? asset.status === "FAILED"
              ? "Голосование и расчёты доступны без картинки"
              : "Сцена появится автоматически"
            : "Сцена будет создана после сохранения инициативы"}
        </small>
        {asset?.status === "FAILED" && canManage && (
          <button
            type="button"
            className="cc-image-regenerate"
            onClick={() =>
              void action("Пробуем создать иллюстрацию инициативы снова…", () =>
                api.regenerateCycleImage(campaignId, asset.id),
              )
            }
          >
            Повторить создание
          </button>
        )}
      </div>
    </div>
  );
}
function initiativeExpectedEffectLabel(kind: string) {
  switch (kind) {
    case "RESOURCE_GAIN":
      return "Получить";
    case "RESOURCE_PRESERVE":
      return "Сохранить";
    case "INDICATOR_GAIN":
      return "Улучшить";
    case "THREAT_REDUCE":
      return "Снизить угрозу";
    case "INSTITUTION_RELIEF":
      return "Разгрузить";
    case "FACTION_RELATION":
      return "Отношение";
    case "KNOWLEDGE_GAIN":
      return "Знание";
    case "NO_DIRECT_RESOURCE":
    default:
      return "Эффект";
  }
}
function InitiativeAssessmentView({
  item,
  compact = false,
  stale = false,
}: {
  item: Initiative;
  compact?: boolean;
  stale?: boolean;
}) {
  const assessment = item.assessment;
  const expectedEffects = assessment.expectedEffects ?? [];
  return (
    <div
      className={`cc-initiative-assessment${compact ? " compact" : ""}${stale ? " stale" : ""}`}
    >
      <header>
        <strong>
          {stale ? "Оценка сохранённой версии" : "Системная оценка"}
        </strong>
        <span>
          {stale
            ? "нужно пересчитать"
            : `${riskLabel(assessment.riskLevel)}${assessment.locked ? " · зафиксирована" : ""}`}
        </span>
      </header>
      {!compact && <p>{assessment.summary}</p>}
      <div className="cc-assessment-groups">
        <div>
          <small>Расходы</small>
          {assessment.resources.length === 0 ? (
            <span>
              <b>Без прямого списания</b>
              <i>0</i>
            </span>
          ) : (
            assessment.resources.map((resource) => (
              <span key={resource.code}>
                <b>{resource.displayName}</b> −{resource.cost}{" "}
                <i>из {resource.available}</i>
              </span>
            ))
          )}
        </div>
        {expectedEffects.length > 0 && (
          <div>
            <small>Ожидаемый эффект</small>
            {expectedEffects.map((effect) => (
              <span key={`${effect.kind}-${effect.code}`}>
                <b>{initiativeExpectedEffectLabel(effect.kind)}</b>{" "}
                {effect.displayName}
              </span>
            ))}
          </div>
        )}
        <div>
          <small>Исполнители</small>
          {assessment.institutions.map((institution) => (
            <span key={institution.code}>
              <b>{institution.displayName}</b>{" "}
              {institution.role === "PRIMARY" ? "основной" : "поддержка"}
            </span>
          ))}
        </div>
        {assessment.factions.length > 0 && (
          <div>
            <small>Фракции</small>
            {assessment.factions.map((faction) => (
              <span key={faction.code}>
                <b>{faction.displayName}</b>{" "}
                {factionStanceLabel(faction.stance)}
              </span>
            ))}
          </div>
        )}
      </div>
      {!compact && assessment.reasons.length > 0 && (
        <details>
          <summary>Почему такая оценка</summary>
          <ul>
            {assessment.reasons.map((reason, index) => (
              <li key={`${index}-${reason.slice(0, 24)}`}>{reason}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
function initiativeDraft(
  existing: Initiative | undefined,
  _setup: CampaignSetup,
): InitiativeDraft {
  return {
    title: existing?.title ?? "",
    description: existing?.description ?? "",
    linkedActionIds: existing?.linkedActionIds ?? [],
    linkedAgreementIds: existing?.linkedAgreementIds ?? [],
  };
}

export {
  ActionInitiativesPanel,
  CouncilInitiativeCreatePanel,
  initiativeDraft,
  AttachmentPicker,
  InitiativeAgendaControlButtons,
  InitiativeDetailModal,
  InitiativeModalVoteControls,
  InitiativeVoteSummary,
  InitiativeDeadlineInline,
  CouncilInitiativeSelectorCard,
  InitiativeThumb,
  InitiativeImage,
  InitiativeAssessmentView,
  initiativeExpectedEffectLabel,
};
export type { CouncilInitiativeShowcaseMode, InitiativeDraft };
